import React, { useRef, useEffect, useCallback } from 'react';
import { Plus, Calendar, Clock, FileText, Search, Edit, Trash2, Lock, Users, Loader2 } from 'lucide-react';
import { useInfiniteScheduleTemplates } from '@/hooks/schedules/useScheduleTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { LoadingTable } from '@/components/ui/loading-states';
import { QueryError } from '@/components/ui/error-state';
import { cn } from '@/lib/utils';
import type { ScheduleTemplate } from '@/types/schedule.types';
import { APP_TIME_ZONE } from '@/utils/dateFormat';

interface TemplatesListProps {
  selectedTemplateId: string | null;
  onSelectTemplate: (template: ScheduleTemplate) => void;
  onCreateTemplate?: () => void;
  onEditTemplate?: (template: ScheduleTemplate) => void;
  onEditWorkTime?: (template: ScheduleTemplate) => void;
  onDeleteTemplate?: (template: ScheduleTemplate) => void;
  onAssignTemplate?: (template: ScheduleTemplate) => void;
}

export const TemplatesList: React.FC<TemplatesListProps> = ({
  selectedTemplateId,
  onSelectTemplate,
  onCreateTemplate,
  onEditTemplate,
  onEditWorkTime,
  onDeleteTemplate,
  onAssignTemplate,
}) => {
  const [searchInput, setSearchInput] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [source, setSource] = React.useState<'all' | '1c' | 'local'>('all');
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Debounce поиска - ждём 1 секунду после последнего ввода
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteScheduleTemplates({
    search: debouncedSearch,
    source,
    limit: 20,
  });

  // Intersection Observer для автоматической подгрузки при скролле
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <div className="p-4">
        <LoadingTable rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <QueryError
          error={error}
          refetch={() => window.location.reload()}
        />
      </div>
    );
  }

  // Собираем все шаблоны из загруженных страниц
  const templates = data?.pages.flatMap(page => page.items) || [];
  const total = data?.pages[0]?.total || 0;

  return (
    <div className="flex h-full flex-col border-r">
      <div className="border-b p-4">
        <h2 className="text-lg font-semibold mb-3">Шаблоны графиков</h2>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Поиск шаблонов..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant={source === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSource('all')}
              className="flex-1"
            >
              Все
            </Button>
            <Button
              variant={source === '1c' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSource('1c')}
              className="flex-1"
            >
              1С
            </Button>
            <Button
              variant={source === 'local' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSource('local')}
              className="flex-1"
            >
              Локальные
            </Button>
          </div>

          {onCreateTemplate && (
            <Button
              onClick={onCreateTemplate}
              className="w-full"
              variant="outline"
            >
              <Plus className="mr-2 h-4 w-4" />
              Создать шаблон
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {templates.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <Calendar className="mx-auto h-12 w-12 mb-3 opacity-50" />
            <p className="text-sm">Нет доступных шаблонов</p>
          </div>
        ) : (
          <div className="p-2">
            {templates.map((template, index) => (
              <div
                key={template.id}
                className={cn(
                  'w-full p-3 transition-colors',
                  selectedTemplateId === template.id && 'bg-muted',
                  index !== templates.length - 1 && 'border-b border-border'
                )}
              >
                <button
                  onClick={() => onSelectTemplate(template)}
                  className="w-full text-left"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{template.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {template.recurrence_type && (
                          <span className="text-xs text-muted-foreground">
                            {template.recurrence_type === 'weekly' ? 'Еженедельный' :
                             template.recurrence_type === 'shift' ? 'Сменный' : 'Пользовательский'}
                          </span>
                        )}
                        {template.work_days_per_week && (
                          <span className="text-xs text-muted-foreground">
                            {template.work_days_per_week} дн/нед
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {template.code_1c && (
                        <Lock 
                          className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" 
                          aria-label="Редактируется только в 1С"
                        />
                      )}
                      <Badge variant={template.code_1c ? 'secondary' : 'outline'}>
                        {template.code_1c ? '1С' : 'Локальный'}
                      </Badge>
                    </div>
                  </div>

                  {template.description && (
                    <p className="mt-2 text-xs text-muted-foreground line-clamp-2">
                      {template.description}
                    </p>
                  )}
                </button>

                <div className="flex items-center gap-1 mt-2 justify-end">
                  {onAssignTemplate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAssignTemplate(template);
                      }}
                      title="Назначить сотрудникам"
                    >
                      <Users className="h-4 w-4" />
                    </Button>
                  )}
                  {onEditWorkTime && template.code_1c && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditWorkTime(template);
                      }}
                      title="Изменить время работы"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Clock className="h-4 w-4" />
                    </Button>
                  )}
                  {onEditTemplate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditTemplate(template);
                      }}
                      title={template.code_1c ? "Просмотр (только чтение)" : "Редактировать"}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  {onDeleteTemplate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteTemplate(template);
                      }}
                      title="Удалить"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {/* Триггер для ленивой загрузки */}
            {hasNextPage && (
              <div ref={loadMoreRef} className="p-4 text-center">
                {isFetchingNextPage ? (
                  <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                ) : (
                  <span className="text-xs text-muted-foreground">
                    Прокрутите для загрузки
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t p-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Загружено: {templates.length} из {total}</span>
          <span>{new Date().toLocaleDateString('ru-RU', { timeZone: APP_TIME_ZONE })}</span>
        </div>
      </div>
    </div>
  );
};